import { useEffect, useMemo } from 'react';
import { t } from 'shared/locale/texts';
import { monthNames, Languages } from '../../../util/calendar';
import type { ReduxAction } from '@finn-no/redux-actions';
import IconCalendar16 from '@warp-ds/icons/react/calendar-16';

import type { MonthPickerMonth, MonthDuration } from '../../../types/calendar';

import { MonthPicker } from '../common/MonthPicker';
import actions from '../../prepackage/prePackageActions';
import { Dropdown } from '../common/Dropdown';
import { FrontpageModal } from '../common/FrontpageModal';
import { CustomSelect } from '../common/CustomSelect';

type FlexibleDateSelectionProps = {
    travelMonths: MonthPickerMonth[];
    duration: MonthDuration;
    dispatch: (action: ReduxAction) => void;
};

export const FlexibleDateSelection = ({ travelMonths, duration, dispatch }: FlexibleDateSelectionProps) => {
    const fullMonthNames = monthNames[Languages.NO as keyof typeof monthNames];
    const shortMonthNames = fullMonthNames.map((n) => n.slice(0, 3));

    const allMonths = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() + i);
            return `${date.getMonth() + 1}-${date.getFullYear()}`;
        });
    }, []);

    useEffect(() => {
        if (travelMonths.length === 0) {
            Array.from({ length: 12 }, (_, i) => {
                const now = new Date();
                const date = new Date(now.getFullYear(), now.getMonth() + i);
                return {
                    month: date.getMonth() + 1,
                    year: date.getFullYear(),
                };
            }).forEach((m) => {
                dispatch(actions.toggleTravelMonth(m));
            });
        }
    }, [travelMonths, dispatch]);

    const getSummaryText = () => {
        const days = `${duration.from} - ${duration.to} dager,`;
        const selected = travelMonths.map((m) => `${m.month}-${m.year}`);
        const allSelected = selected.length === 12 && allMonths.every((key) => selected.includes(key));

        if (travelMonths.length === 0 || allSelected) {
            return `${days} når som helst`;
        }

        if (travelMonths.length === 1) {
            return `${days} i ${shortMonthNames[travelMonths[0].month - 1]}`;
        }

        return `${days} i ${travelMonths.map((m) => shortMonthNames[m.month - 1]).join(', ')}`;
    };

    const renderValue = (val: number): string => `${val} ${val === 1 ? 'dag' : 'dager'}`;

    const generateOptions = (min: number, max: number, filter: (val: number) => boolean) =>
        Array.from({ length: max - min + 1 }, (_, i) => min + i)
            .filter(filter)
            .map((val) => ({
                value: val.toString(),
                label: renderValue(val),
            }));

    const fromOptions = generateOptions(1, duration.to - 1, (val) => val < duration.to);
    const toOptions = generateOptions(duration.from, 35, () => true);

    const updateDuration = (partial: Partial<MonthDuration>) => {
        dispatch(actions.setDuration({ ...duration, ...partial }));
    };

    const renderDurationControls = () => (
        <div className="flex flex-col gap-16">
            <strong>{t('prepackage.duration')}</strong>
            <div className="flex flex-row w-full gap-8">
                <CustomSelect
                    relative
                    triggerLabel="Fra"
                    options={fromOptions}
                    value={duration.from.toString()}
                    onChange={(val) => updateDuration({ from: Number.parseInt(val, 10) })}
                    fullscreenTitle="Hvor lenge skal turen vare?"
                />
                <CustomSelect
                    relative
                    triggerLabel="Til"
                    options={toOptions}
                    value={duration.to.toString()}
                    onChange={(val) => updateDuration({ to: Number.parseInt(val, 10) })}
                    fullscreenTitle="Hvor lenge skal turen vare?"
                />
            </div>
            <MonthPicker title="Aktuelle måneder" travelMonths={travelMonths} duration={duration} dispatch={dispatch} />
        </div>
    );

    return (
        <>
            <Dropdown prefix={<IconCalendar16 />} trigger={{ text: getSummaryText(), label: 'Fleksibel dato' }} className="hidden sm:flex">
                {renderDurationControls()}
            </Dropdown>
            <FrontpageModal breakpoint="sm" title="Når vil du reise?" trigger={{ text: getSummaryText(), prefix: <IconCalendar16 /> }}>
                {renderDurationControls()}
            </FrontpageModal>
        </>
    );
};
